import { Injectable } from '@angular/core';
import { App as CapApp } from '@capacitor/app';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { DeliveredNotificationSchema, LocalNotifications, PermissionStatus } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import Bowser from 'bowser';
import { DateTime } from 'luxon';
import { formatLogTimestamp } from '@cf-shared/date-format-machine';
import { isCapacitorNative } from 'environments/environment-helpers';
import { AppVersion } from 'app/version';
import { SettingsService } from './settings.service';
import { UserDb } from './user-db.service';
import { CloudFunctionsService } from './cloud-functions.service';
import { NavController } from 'providers/nav-controller';
import { ITrainingPage } from 'pages/training/training-interface';
import { IWorkoutDetailPage } from 'pages/workout-detail/workout-detail-interface';
import { IEditWeighInPage } from 'pages/edit-weigh-in/edit-weigh-in-interface';
import { IWeeklyCheckupPage } from 'pages/weekly-checkup/weekly-checkup-interface';
import { IHomePage, HomeTab } from 'pages/home/home-interface';
import { IEditMealPage } from 'pages/edit-meal/edit-meal-interface';
import { ICoachSettingsPage } from 'pages/coach-settings/coach-settings-interface';
import { IReferralHubPage } from 'pages/referral-hub/referral-hub-interface';
import { parseCoachManageDeepLink } from './coach-push-deep-link';
import { LL } from 'i18n/i18n.service';
import { ScheduleEntry } from '@cf-shared/reminders-interface';
import { ADVANCE_MINUTES, APP_TITLE, buildEntrySuppressionKey, obtainFcmTokenAsync, buildNotificationsFromLogEntries, buildReminderStringId, decideLocalNavigation, decideReminderRestore, decidePushNavigation, LocalNotificationExtra, NavTarget, NotificationEvent, readPushLogEntryId, registerPushTokenAsync, resolveLogEntryIdFromStringId, SignInWait, stringToNotificationId, waitForSignInAsync, WEEKDAY_NAMES } from './notification-builder';
import { getPersistedDeviceId } from './local-storage-hygiene';
import { Log_Entry } from '@cf-shared/data-model/user-log';
import { describeError } from '@cf-shared/error-describe';
import { isReminderServerFailureExpected } from './reminder-server-failure';
import { isDatabaseTornDown } from './database-unavailable-error';
import { LoggingService } from './logging.service';
import { decidePushTapFailureHandling, PUSH_TAP_READY_WAIT_LIMIT_MS, PushTapFailureHandling, PushTapReadyGate, runPushTapWhenReadyAsync } from './push-tap-ready-gate';
import { ConnectionState, OnlineStateService } from './online-state.service';
import { AuthService } from './auth.service';

interface FcmTokenPluginInterface {
  getToken(): Promise<{ value: string }>;
}

// minimal structural type for the telemetry sink that the listener methods take as a
// parameter (setupDeepLinkListener, setupPushListener, handlePushTapAsync). problems are
// reported through the injected LoggingService, never through a parameter.
interface PushTelemetry {
  trackEvent(name: string, params: Record<string, any>): void;
}

// native plugin that returns the FCM token on iOS (where PushNotifications returns APNs token)
const FcmTokenPlugin = registerPlugin<FcmTokenPluginInterface>('FcmTokenPlugin');

// how long the reminder calls wait for Firebase sign-in to settle on a warm resume before they go
// out anyway. no user action waits on those calls, so this can be generous (see waitForSignInAsync)
const SIGN_IN_SETTLE_WAIT_MS = 30_000;

// every destination a notification tap can open, except `none` (which opens nothing)
type OpenableNavTarget = Exclude<NavTarget['target'], 'none'>;

// Where a tapped notification goes. Written ONCE for both delivery routes — a reminder this
// device scheduled and a push the server sent both land here, each having decided its
// destination with its own pure decider in notification-builder.ts.
//
// The mapped type is what makes this worth doing: adding a destination to `NavTarget`
// without adding a row here is a COMPILE ERROR. Before, a destination wired into only one
// of two switch statements was a tap that silently did nothing on the other route (#4963
// needed exactly that pair of edits, and a prose comment was what kept them in step).
//
// Each row receives its own narrowed target, so `nav.id` and `nav.day` are only reachable on
// the destinations that really carry them.
type NavHandlers = { [K in OpenableNavTarget]: (navCtrl: NavController, nav: Extract<NavTarget, { target: K }>) => void };

const NAV_HANDLERS: NavHandlers = {
  training:         (navCtrl, nav) => { void navCtrl.pushAsync(ITrainingPage, { day: nav.day }); },
  meal:             (navCtrl, nav) => { void navCtrl.pushAsync(IEditMealPage, { id: nav.id, day: nav.day }); },
  'weigh-in':       (navCtrl, nav) => { void navCtrl.pushAsync(IEditWeighInPage, { id: nav.id, day: nav.day }); },
  'weekly-checkup': (navCtrl, nav) => { void navCtrl.pushAsync(IWeeklyCheckupPage, { id: nav.id }); },
  home:             (navCtrl, nav) => { void navCtrl.pushAsync(IHomePage, { tab: HomeTab.DailyLog, day: nav.day }); },
  'coach-settings': (navCtrl) => { void navCtrl.pushAsync(ICoachSettingsPage, {}); },
  'referral-hub':   (navCtrl) => { void navCtrl.pushAsync(IReferralHubPage, {}); },
  'workout-detail': (navCtrl, nav) => { void navCtrl.pushAsync(IWorkoutDetailPage, { id: nav.workoutId }); },
};

// open the page a decided destination names. the cast is needed because TypeScript checks
// the looked-up row and the value independently, and so cannot see that both are narrowed by
// the same `nav.target`; the table above is what guarantees a row exists for every one.
function navigateToTarget(navCtrl: NavController, nav: NavTarget): void {
  if (nav.target === 'none') return;
  const openPage = NAV_HANDLERS[nav.target] as (c: NavController, n: NavTarget) => void;
  openPage(navCtrl, nav);
}

// true when running under the Android `.local` product flavor (applicationId
// suffix `.local`). returns false on iOS without calling App.getInfo(); on
// Android, App.getInfo() rejection propagates — the caller is expected to be
// inside a try/catch (registerFcmTokenAsync swallows it alongside other
// transient registration errors)
async function isLocalAndroidFlavorAsync(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'android') return false;
  const appInfo = await CapApp.getInfo();
  return appInfo.id.endsWith('.local');
}

interface AppSettingsPluginInterface {
  open(): Promise<void>;
}

const AppSettingsPlugin = registerPlugin<AppSettingsPluginInterface>('AppSettingsPlugin');

const CONTENT_BODY_GETTERS: Record<string, () => string> = {
  workout: () => LL.settings.notifications.content.workout(),
  meal: () => LL.settings.notifications.content.meal(),
  weighIn: () => LL.settings.notifications.content.weighIn(),
  weeklyCheckup: () => LL.settings.notifications.content.weeklyCheckup(),
  sleep: () => LL.settings.notifications.content.sleep(),
};

// the upstream Capacitor `DeliveredNotificationSchema` only declares `id`, `tag?`, `title`, `body` etc.,
// but at runtime each delivered entry also carries the original `extra` payload (the stale-cleanup
// reads `extra.day` to find yesterday's entries). Narrow via intersection so we keep the full schema
// for the write-back to `removeDeliveredNotifications` without an `as any` cast.
type DeliveredEntry = DeliveredNotificationSchema & { extra?: { day?: string } };

// one key per reminder occurrence: which reminder, on which local day
function suppressionSlot(stringId: string, localDay: string): string {
  return `${stringId}|${localDay}`;
}

@Injectable()
export class NotificationService {
  private navCtrl: NavController;
  private fcmListenersRegistered = false;

  // the suppress and undo operations still running or waiting, one chain per reminder
  // occurrence (see suppressionSlot). every suppress and every undo for the same occurrence runs
  // strictly after the one requested before it, and each one settles only after its server call
  // has answered. without that, a quick "skip, put back, skip again" can land out of order: a
  // clear reaching the server before the stamp it should undo (the push stays silenced all day),
  // or an undo scheduling the local reminder after a later skip already cancelled it.
  private reminderChains = new Map<string, Promise<void>>();

  // #6180: a push tap that launches the app arrives before the user database is loaded and
  // before the navigation restore has run, so it waits here until the boot says the
  // signed-in app is ready (see markSignedInAppReady)
  private pushTapReadyGate = new PushTapReadyGate();

  // how long waitForSignInAsync waits; a field only so the unit tests can shorten it
  signInSettleWaitMs = SIGN_IN_SETTLE_WAIT_MS;

  constructor(private cloudFunctions: CloudFunctionsService, private logging: LoggingService, private onlineState: OnlineStateService, private auth: AuthService) {}

  // waits for Firebase sign-in to settle before a reminder call goes to the server (df-d57fd669a03f)
  private waitForSignInAsync(): Promise<SignInWait> {
    return waitForSignInAsync({
      settled: this.auth.whenFirebaseAuthSettled,
      isSignedOut: () => this.auth.isLikelySignOutRace(),
      waitMs: this.signInSettleWaitMs,
    });
  }

  // queue `op` behind whatever is already queued for `slot`. `op` must never reject — both callers
  // catch everything themselves — so one failure cannot stop the operations queued after it.
  private runInRequestOrder(slot: string, op: () => Promise<void>): Promise<void> {
    const previous = this.reminderChains.get(slot) ?? Promise.resolve();
    const run = previous.then(op, op);
    this.reminderChains.set(slot, run);
    const forget = () => {
      // a later operation for the same slot may have replaced this one — leave that one alone
      if (this.reminderChains.get(slot) === run) this.reminderChains.delete(slot);
    };
    void run.then(forget, forget);
    return run;
  }

  // user has acted on the event scheduled by `entry` — cancel the pending local notification,
  // dismiss any matching delivered local-notification tray entry, and tell the server to skip
  // today's FCM fire for the matching schedule doc. fire-and-forget; never blocks the caller.
  // silently no-ops for entries with no reminder config (e.g. DayStart).
  async suppressForEntryAsync(entry: Log_Entry, now_local: DateTime): Promise<void> {
    const key = buildEntrySuppressionKey(entry, now_local);
    if (!key) return;
    // suppressScheduledAsync catches every failure it meets, so it never rejects
    await this.runInRequestOrder(suppressionSlot(key.stringId, key.localDay),
      () => this.suppressScheduledAsync(key.stringId, key.localDay, key.utcDay, key.scheduleKey));
  }

  // the user UNDID the action that suppressed `entry`'s reminder (put a skipped or done entry
  // back) — the undo of suppressForEntryAsync. if the reminder has not fired yet today, schedule
  // the local notification again and tell the server to clear today's suppressedDay stamp, so
  // both the on-device reminder and the server push arrive after all. fire-and-forget like the
  // suppress; never blocks the caller.
  //
  // whether to restore is decided when the undo actually runs, not when it was asked for: it may
  // wait behind a suppress still talking to the server, and by then the fire time may have passed
  // or the entry may have been skipped again. so it takes a clock rather than a fixed time, and
  // reads the entry's state at that moment.
  async unsuppressForEntryAsync(entry: Log_Entry, settings: SettingsService, getNowLocal: () => DateTime): Promise<void> {
    const key = buildEntrySuppressionKey(entry, getNowLocal());
    if (!key) return;
    await this.runInRequestOrder(suppressionSlot(key.stringId, key.localDay), async () => {
      try {
        await this.restoreReminderAsync(entry, settings, getNowLocal());
      } catch (err) {
        // restoreReminderAsync handles the failures it expects; this is anything else. caught
        // here so the operations queued behind this one still run.
        this.logging.trackSilentProblem('ReminderUnsuppressFailed', { stringId: key.stringId, day: key.localDay, error: describeError(err) });
      }
    });
  }

  private async restoreReminderAsync(entry: Log_Entry, settings: SettingsService, now_local: DateTime): Promise<void> {
    const restore = decideReminderRestore(entry, settings, now_local, key => CONTENT_BODY_GETTERS[key]?.() ?? key);
    if (!restore) return;
    const { notification, key } = restore;

    // awaited, so an operation queued behind this one starts only after the server answered.
    // optional-chain for the same early-init reason as suppressScheduledAsync
    // if the device is offline, losing today's server push is the expected cost: the on-device
    // reminder below still arrives
    if (this.cloudFunctions?.remindersUnsuppressAsync) {
      const cloudFunctions = this.cloudFunctions;
      await this.sendReminderStampAfterSignInAsync('unsuppress',
        () => cloudFunctions.remindersUnsuppressAsync({ stringId: key.stringId, day: key.utcDay, scheduleKey: key.scheduleKey }),
        'ReminderUnsuppressServerFailed', { stringId: key.stringId, day: key.utcDay });
    }

    if (!isCapacitorNative()) return;
    try {
      // never prompt here — an undo is not the moment to ask for permission
      const permStatus = await LocalNotifications.checkPermissions();
      if (permStatus.display !== 'granted') return;
      // same id as the notification the suppress cancelled, so this cannot duplicate it.
      // `sound: 'default'` for the iOS reason given in rescheduleAllAsync
      await LocalNotifications.schedule({ notifications: [{ ...notification, sound: 'default' }] });
    } catch (err) {
      // the reminder the user just asked for back is missing on this device until the next app open
      this.logging.trackSilentProblem('ReminderUnsuppressRescheduleFailed', { stringId: key.stringId, day: key.localDay, error: describeError(err) });
    }
  }

  // lower-level form for callers that already have the identifiers (e.g. test notifications).
  // - localDay: entry's local date (matches how the pending local notification's numericId was derived)
  // - utcDay: UTC date of the fire minute (matches what tickNotifications compares against)
  // - scheduleKey: schedule doc _id prefix `${utcWeekday}-${HH:MM}` — lets the server do a direct couchGet
  async suppressScheduledAsync(stringId: string, localDay: string, utcDay: string, scheduleKey: string): Promise<void> {
    if (!stringId || !localDay || !utcDay || !scheduleKey) return;

    const numericId = stringToNotificationId(`${stringId}-${localDay}`);

    if (isCapacitorNative()) {
      // cancel pending (not yet fired) local notification
      try {
        await LocalNotifications.cancel({ notifications: [{ id: numericId }] });
      } catch (err) {
        // the user already acted on this entry, but its reminder will still fire
        this.logging.trackSilentProblem('ReminderSuppressLocalFailed', { step: 'cancel', stringId, day: localDay, error: describeError(err) });
      }

      // dismiss already-delivered local-notification tray entry that matches our numericId / tag=stringId.
      // server FCM pushes are delivered via PushNotifications and are NOT visible here — Capacitor's
      // PushNotifications.removeDeliveredNotifications has no per-id selector on Android (only iOS),
      // so server-pushed tray entries that already fired before suppression remain visible until the
      // user dismisses them. The local-side dismissal still helps: when the device was offline and
      // only the local notification fired, this clears it; when both fired, the OS already deduped
      // them via the (tag, id) tuple so dismissing the local one removes the only visible entry.
      try {
        const delivered = await LocalNotifications.getDeliveredNotifications();
        const toRemove = (delivered.notifications as DeliveredEntry[]).filter(n => n.id === numericId || n.tag === stringId);
        if (toRemove.length > 0) {
          await LocalNotifications.removeDeliveredNotifications({ notifications: toRemove });
        }
      } catch (err) {
        // the reminder the user already acted on stays visible in the tray
        this.logging.trackSilentProblem('ReminderSuppressLocalFailed', { step: 'removeDelivered', stringId, day: localDay, error: describeError(err) });
      }
    }

    // tell the server to skip today's fire. awaited, so this method settles only once the server
    // has answered — the request-order chain (runInRequestOrder) relies on that; every
    // caller still fires this method and forgets it, so nobody else waits. the helper never
    // rejects, so a failure cannot become an unhandled promise rejection. offline, the on-device
    // cancel above is all we can do and today's server push still arrives.
    // optional-chain on `remindersSuppressAsync`: CloudFunctionsService initializes its callable
    // bindings asynchronously in initAsync(), so a very early call (before init completes) can
    // legitimately see the field as null.
    // the wait for Firebase sign-in comes after the on-device steps above, so a slow sign-in never
    // delays the cancel; it does hold back an undo queued behind this one, which is safe because
    // the undo decides on the clock when it runs (df-c4892fdc42b5)
    if (this.cloudFunctions?.remindersSuppressAsync) {
      const cloudFunctions = this.cloudFunctions;
      await this.sendReminderStampAfterSignInAsync('suppress',
        () => cloudFunctions.remindersSuppressAsync({ stringId, day: utcDay, scheduleKey }),
        'ReminderSuppressServerFailed', { stringId, day: utcDay });
    }
  }

  // sends one skip or undo call to the server once Firebase sign-in has settled: right after a
  // launch Firebase may not have its user yet, and the call would go out with no token
  // (df-c4892fdc42b5). never rejects, so the request-order chain keeps going after a failure.
  // if nobody is signed in any more, there is nothing to send for, so it sends nothing and reports
  // nothing. a failure the cloud-functions layer already reported, or skipped while offline, is
  // only logged; anything else is a real failure to set or clear the stamp, so it is reported.
  private async sendReminderStampAfterSignInAsync(what: 'suppress' | 'unsuppress', send: () => Promise<unknown>,
    problemName: string, context: Record<string, unknown>): Promise<void> {
    let signIn = SignInWait.NotSettled;
    try {
      signIn = await this.waitForSignInAsync();
      if (signIn === SignInWait.SignedOut) {
        console.log(`NotificationService: ${what} not sent to the server, nobody is signed in`);
        return;
      }
      await send();
    } catch (err) {
      if (isReminderServerFailureExpected(err)) {
        console.log(`NotificationService: ${what} server call failed, offline or already handled:`, err);
        return;
      }
      // signInSettled false means Firebase never settled, so the failure echoes that stall
      this.logging.trackSilentProblem(problemName, { ...context, error: describeError(err), signInSettled: signIn !== SignInWait.NotSettled });
    }
  }

  async cancelAllAsync(): Promise<void> {
    if (!isCapacitorNative()) return;
    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
        console.log(`NotificationService: cancelled ${pending.notifications.length} notifications`);
      }
    } catch (err) {
      // the only caller is the private-data purge, which carries on: the reminders it meant to
      // remove keep firing for data that is about to be deleted
      this.logging.trackSilentProblem('NotificationCancelAllFailed', { error: describeError(err) });
    }
  }

  async requestPermissionAsync(): Promise<PermissionStatus | null> {
    if (!isCapacitorNative()) return null;

    try {
      return await LocalNotifications.requestPermissions();
    } catch (err) {
      // the plugin threw instead of answering; callers read null as "not granted"
      this.logging.trackSilentProblem('NotificationPermissionRequestFailed', { error: describeError(err) });
      return null;
    }
  }

  async openDeviceSettingsAsync(): Promise<void> {
    if (!isCapacitorNative()) return;
    try {
      await AppSettingsPlugin.open();
    } catch (err) {
      // the user tapped to open the device settings and nothing happened
      this.logging.trackSilentProblem('OpenDeviceSettingsFailed', { error: describeError(err) });
    }
  }

  // build a test notification that fires 2 minutes from now and feed it through the normal rescheduleAllAsync path
  async scheduleTestNotificationAsync(userDb: UserDb, settings: SettingsService, now_local: DateTime, cloudFunctions: CloudFunctionsService): Promise<void> {
    const browserInfo = Bowser.getParser(window.navigator.userAgent).getResult();
    const platform = isCapacitorNative() ? `native/${Capacitor.getPlatform()}` : 'web';
    const sentAt = formatLogTimestamp(DateTime.local());
    const deviceLabel = `${browserInfo.os.name} ${browserInfo.os.version}, ${browserInfo.browser.name}, ${platform}, ${AppVersion}`;
    const testBody = `[TEST ${sentAt}] from ${deviceLabel}`;

    // reverse-engineer a fake entry time from the fire time so the stringId format matches real notifications
    // (real notifications: fire at entryTime - ADVANCE_MINUTES, stringId based on entryTime)
    const fireTime_local = now_local.plus({ minutes: 2 });
    const entryTime_local = fireTime_local.plus({ minutes: ADVANCE_MINUTES });
    const dateISO = entryTime_local.toISODate()!;
    const stringId = buildReminderStringId('test', entryTime_local);
    const numericId = stringToNotificationId(`${stringId}-${dateISO}`);

    const testNotification: NotificationEvent = {
      id: numericId,
      title: APP_TITLE,
      body: testBody,
      schedule: { at: fireTime_local.toJSDate() },
      extra: { page: 'home', stringId, logEntryId: '', day: dateISO },
    };

    // on native, go through the full reschedule path (local + server sync)
    // on web, only sync to server for FCM push to all registered devices
    if (isCapacitorNative()) {
      await this.rescheduleAllAsync(userDb, settings, now_local, cloudFunctions, [testNotification]);
    } else {
      void this.syncScheduleToServerAsync([testNotification], cloudFunctions);
    }
  }

  async requestPermissionAndScheduleAsync(userDb: UserDb, settings: SettingsService, now_local: DateTime, cloudFunctions?: CloudFunctionsService): Promise<void> {
    if (!isCapacitorNative()) return;
    const result = await this.requestPermissionAsync();
    if (result?.display === 'granted') {
      await this.rescheduleAllAsync(userDb, settings, now_local, cloudFunctions);
    }
  }

  setupDeepLinkListener(navCtrl: NavController, telemetry: PushTelemetry) {
    if (!isCapacitorNative()) return;
    this.navCtrl = navCtrl;

    void LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
      const extra: LocalNotificationExtra | undefined = action.notification.extra;
      const page = extra?.page;
      if (!extra || !page || !this.navCtrl) return;

      telemetry.trackEvent('notification_tapped', {
        notification_type: page,
      });

      // pure routing decision (tested in notification.service.test.ts). for entry-specific
      // pages with no log entry id, the target flips to `home` with unresolvedFallback set —
      // same rule as the push route, so we report it the same way and land the user on the
      // daily log rather than opening an editor with no subject.
      const nav = decideLocalNavigation(extra, this.navCtrl.getCurrentPage());
      if (nav.target === 'home' && nav.unresolvedFallback) {
        this.logging.trackSilentProblem('LocalNotificationLogEntryUnresolved', { page, day: extra.day });
      }
      navigateToTarget(this.navCtrl, nav);
    });
  }

  // request push permission, register with FCM, and send token to server.
  // registration failures are reported to Sentry — without a working FCM registration
  // this device silently never receives server pushes.
  async registerFcmTokenAsync(cloudFunctions: CloudFunctionsService): Promise<void> {
    if (!isCapacitorNative()) return;

    try {
      // skip FCM on the Android `local` build flavor — it shares the dev Firebase
      // project for OAuth (Google Sign-In) but must not claim FCM tokens against
      // it from iteration devices. see registerFcmTokenAsync gate notes in
      // design-docs/push-notifications.md
      if (await isLocalAndroidFlavorAsync()) {
        console.log('NotificationService: skipping FCM registration (local flavor)');
        return;
      }

      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') return;

      const platform = Capacitor.getPlatform() as 'ios' | 'android';

      if (platform === 'ios') {
        // on iOS, PushNotifications.register() kicks off APNs registration and returns immediately.
        // APNs responds asynchronously via AppDelegate, which forwards to Capacitor as a `registration`
        // event — that's our signal that Firebase Messaging has the APNs token and can mint an FCM token.
        // a blind poll right after register() races APNs (takes up to ~15s on first registration).
        if (!this.fcmListenersRegistered) {
          this.fcmListenersRegistered = true;
          // fires when APNs registration completes — at this point Firebase Messaging has
          // the APNs token (set by AppDelegate) and can mint an FCM token. short retry loop
          // covers Firebase's internal APNs-to-FCM exchange latency.
          void PushNotifications.addListener('registration', async (registrationToken) => {
            // on iOS the registration event's value is the APNs device token — in
            // practice stable per physical device (unlike the FCM token, which rotates
            // on reinstall), though Apple documents it MAY rotate on reinstall/restore/
            // OS update. we send it as deviceId so the server replaces this device's
            // old token entry instead of appending (#3714 duplicate-push fix); if it
            // does rotate, the server's 60-day staleness sweep is the backstop.
            const deviceId = registrationToken.value || getPersistedDeviceId();
            await obtainFcmTokenAsync({
              getToken: async () => (await FcmTokenPlugin.getToken()).value,
              registerToken: token => void registerPushTokenAsync({
                waitForSignIn: () => this.waitForSignInAsync(),
                register: () => cloudFunctions.remindersRegisterDeviceAsync({ token, platform, deviceId }),
                reportProblem: context => this.logging.trackSilentProblem('PushTokenRegistrationFailed', { platform, ...context }),
              }),
              wait: ms => new Promise(r => setTimeout(r, ms)),
              // a fresh ping of our own server, the same check CloudFunctionsService runs before
              // reporting a network error. bounded by the ping's own timeout, so it cannot hang.
              isOfflineAsync: async () =>
                (await this.onlineState.verifyOnlineStatus(ConnectionState.Offline, 'suspected')) === ConnectionState.Offline,
              reportProblem: context => this.logging.trackSilentProblem('FcmTokenUnavailableAfterApnsRegistration', { platform, ...context }),
              now: () => DateTime.now().toMillis(),
            });
          });
          // APNs-side registration failure (distinct from FCM getToken failure above).
          // non-fatal — local notifications still work; only server-driven pushes are lost.
          void PushNotifications.addListener('registrationError', (error) => {
            console.log('NotificationService: APNs registration failed:', error);
            this.logging.trackSilentProblem('PushRegistrationError', { platform, message: (error as any)?.error ?? String(error) });
          });
        }
        await PushNotifications.register();
      } else {
        // on Android, PushNotifications.register() returns FCM token directly
        // register listeners once to avoid accumulation across app opens
        if (!this.fcmListenersRegistered) {
          this.fcmListenersRegistered = true;

          void PushNotifications.addListener('registration', (registrationToken) => {
            // on Android the registration value IS the FCM token (rotates on reinstall),
            // so it can't serve as the stable deviceId — use a persisted UUID instead
            void registerPushTokenAsync({
              waitForSignIn: () => this.waitForSignInAsync(),
              register: () => cloudFunctions.remindersRegisterDeviceAsync({ token: registrationToken.value, platform, deviceId: getPersistedDeviceId() }),
              reportProblem: context => this.logging.trackSilentProblem('PushTokenRegistrationFailed', { platform, ...context }),
            });
          });

          void PushNotifications.addListener('registrationError', (error) => {
            console.log('NotificationService: FCM registration failed:', error);
            this.logging.trackSilentProblem('PushRegistrationError', { platform, message: (error as any)?.error ?? String(error) });
          });
        }

        await PushNotifications.register();
      }
    } catch (err) {
      // permission request, native register(), or the flavor check failed —
      // server push will silently never reach this device, so report it
      console.log('NotificationService: registerFcmToken failed:', err);
      this.logging.trackSilentProblem('FcmRegistrationSetupFailed', { message: (err as any)?.message ?? String(err) });
    }
  }

  async syncScheduleToServerAsync(notifications: NotificationEvent[], cloudFunctions: CloudFunctionsService): Promise<void> {
    let signIn = SignInWait.NotSettled;
    try {
      // on a warm resume Firebase may not have its user yet, and the call would go out with no token
      signIn = await this.waitForSignInAsync();
      // if nobody is signed in any more, there is no schedule to send and the sign-out path owns the teardown
      if (signIn === SignInWait.SignedOut) {
        console.log('NotificationService: schedule not sent to the server, nobody is signed in');
        return;
      }

      const schedules: ScheduleEntry[] = notifications.map(n => {
        // convert fire time to UTC to get day-of-week and HH:MM
        const fireTimeUtc = DateTime.fromJSDate(n.schedule.at).toUTC();
        const utcDay = WEEKDAY_NAMES[fireTimeUtc.weekday - 1];
        const utcTime = `${String(fireTimeUtc.hour).padStart(2, '0')}:${String(fireTimeUtc.minute).padStart(2, '0')}`;

        return {
          stringId: n.extra.stringId,
          utcDay,
          utcTime,
          type: n.extra.page,
          title: n.title,
          body: n.body,
          deepLinkPage: n.extra.page,
          // match the local notification's UNNotificationRequest.identifier on iOS so
          // the server can echo it in apns-collapse-id for push-vs-local dedup.
          numericId: n.id,
        };
      });

      await cloudFunctions.remindersScheduleAsync({ schedules });
    } catch (err) {
      // offline is expected (this runs on every app open) and the on-device reminders still
      // fire; a failure the cloud-functions layer already reported (or skipped while offline) is
      // only logged; anything else leaves the server's push schedule stale, so report it
      if (isReminderServerFailureExpected(err)) {
        console.log('NotificationService: syncScheduleToServer failed, offline or already handled:', err);
        return;
      }
      // signInSettled false means Firebase never settled, so the failure echoes that stall
      this.logging.trackSilentProblem('ReminderScheduleSyncFailed', {
        count: notifications.length,
        error: describeError(err),
        signInSettled: signIn !== SignInWait.NotSettled,
      });
    }
  }

  /**
   * Called by the boot once a signed-in app has finished loading and the right page is on
   * screen (cold Ready, warm resume after the navigation restore, or the restore rescue).
   * Push taps waiting for that moment run now; later ones run at once (#6180).
   */
  markSignedInAppReady() {
    this.pushTapReadyGate.markReady();
  }

  /**
   * Resolves once the signed-in app is ready, the same moment push taps wait for, with no
   * time limit. For notification links remembered while the app was starting (#6573).
   */
  whenSignedInAppReadyAsync(): Promise<void> {
    return this.pushTapReadyGate.waitUntilReadyAsync();
  }

  /**
   * Keeps push taps waiting until `work` settles, even once the app is marked ready. Used by
   * the navigation-restore rescue, whose re-root to home would replace a page a tap opened
   * while it runs (#6180).
   */
  holdPushTapsUntil(work: Promise<unknown>) {
    this.pushTapReadyGate.holdUntil(work);
  }

  setupPushListener(
    navCtrl: NavController,
    userDb: UserDb,
    telemetry: PushTelemetry,
    coachManageHandler?: (clientUid: string, page?: string) => Promise<void>,
  ) {
    if (!isCapacitorNative()) return;

    void PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      // wrap in try/catch — the push listener callback is fire-and-forget from the native
      // side. without the guard, any throw inside handlePushTapAsync (malformed payload,
      // a failed entry lookup, nav failure) would surface as an unhandled rejection
      // and the tap would be silently lost. on failure, report to sentry and at least land
      // the user on home so they're not stranded. a tap cut short by a sign-out is the one
      // exception, handled first below (df-72eb80b3f2c5).
      void this.handlePushTapAsync(action, navCtrl, userDb, telemetry, coachManageHandler)
        .catch((err) => {
          const data = action?.notification?.data;
          // if a sign-out shut the user database down under the tap, the person is gone and the
          // app is reloading to the login page: record it quietly and do not navigate
          if (decidePushTapFailureHandling(err) === PushTapFailureHandling.DropForSignOut) {
            this.logging.trackSilentInfo('PushTapDroppedDuringSignOut', {
              page: data?.page,
              day: data?.day,
              stringId: data?.stringId,
              error: describeError(err),
            });
            return;
          }
          this.logging.trackSilentProblem('PushNotificationHandlerFailed', {
            page: data?.page,
            day: data?.day,
            stringId: data?.stringId,
            error: err?.message || String(err),
          });
          try {
            void navCtrl.pushAsync(IHomePage, { tab: HomeTab.DailyLog, day: data?.day });
          } catch (navErr) {
            // last-ditch fallback — if even the nav fails there's nothing more we can do for
            // the user, who is left where they were
            this.logging.trackSilentProblem('PushNotificationFallbackNavFailed', { page: data?.page, day: data?.day, error: describeError(navErr) });
          }
        });
    });
  }

  private async handlePushTapAsync(
    action: { notification: { data: any } },
    navCtrl: NavController,
    userDb: UserDb,
    telemetry: PushTelemetry,
    coachManageHandler?: (clientUid: string, page?: string) => Promise<void>,
  ): Promise<void> {
    const data = action.notification.data;
    const page = data?.page;
    if (!page || !navCtrl) return;

    const day: string | undefined = data.day;
    const stringId: string | undefined = data.stringId;

    telemetry.trackEvent('push_notification_tapped', {
      notification_type: page,
    });

    // #6180: a tap that launched the app arrives before the user database is loaded (the
    // lookup below threw on it) and before the navigation restore (which would replace the
    // page we open). wait for the signed-in app to be ready. if it never gets there in time
    // (nobody signed in, or a stuck boot the boot watchdogs already report), drop the tap
    // rather than navigate over a page that is not ready.
    //
    // a coach deep link waits too (#6567): entering the client's account reads the coach's
    // roster for the client's name, and before the database is open that read failed, so the
    // "Managing" banner showed the client's raw account id for the whole session
    const coachLink = parseCoachManageDeepLink(page);
    await runPushTapWhenReadyAsync(
      this.pushTapReadyGate,
      PUSH_TAP_READY_WAIT_LIMIT_MS,
      () => (coachLink && coachManageHandler)
        ? coachManageHandler(coachLink.clientUid, coachLink.subPage)
        : this.openPushTapTargetAsync(navCtrl, userDb, page, day, stringId, readPushLogEntryId(data)),
      waitedMs => this.logging.trackSilentInfo('PushTapAppNeverReady', { page, waited_ms: waitedMs }),
    );
  }

  // the part of a push tap that reads the user database and navigates, so it runs only once
  // the signed-in app is ready (#6180)
  private async openPushTapTargetAsync(
    navCtrl: NavController, userDb: UserDb, page: string, day: string | undefined, stringId: string | undefined,
    payloadLogEntryId: string | undefined,
  ): Promise<void> {
    // resolve the specific log entry id from (day, stringId) so each push tap opens the
    // same event it refers to — reminder payloads don't carry logEntryId (ids differ per
    // week and the schedule doc is keyed weekly), so we reconstruct it on the client.
    // stringId format: `{type}-{weekday}-{HHMM}` where HHMM is the entry's local time.
    //
    // a push about ONE known entry names it in its own payload instead (#6433): the coach's
    // "check-up reviewed" push has no day or reminder time to look the check-up up by.
    const id = payloadLogEntryId
      ?? ((day && stringId) ? await resolveLogEntryIdFromStringId(userDb, day, stringId, page) : undefined);

    // pure routing decision (tested in notification.service.test.ts): for entry-specific
    // pages with no resolved id, the target flips to `home` with unresolvedFallback set
    // so we track it as a silent problem and land the user on the daily log.
    const nav = decidePushNavigation(page, day, id);
    if (nav.target === 'home' && nav.unresolvedFallback) {
      this.logging.trackSilentProblem('PushNotificationLogEntryUnresolved', { page, day, stringId });
    }

    navigateToTarget(navCtrl, nav);
  }

  async rescheduleAllAsync(
    userDb: UserDb, settings: SettingsService, now_local: DateTime,
    cloudFunctions?: CloudFunctionsService, extraNotifications?: NotificationEvent[],
  ): Promise<void> {
    if (!isCapacitorNative()) return;

    try {
      // if permission not yet determined, request it (existing users who completed intake before this feature)
      let permStatus = await LocalNotifications.checkPermissions();
      if (permStatus.display === 'prompt' || permStatus.display === 'prompt-with-rationale') {
        permStatus = await LocalNotifications.requestPermissions();
      }
      if (permStatus.display !== 'granted') return;

      // cancel all existing notifications
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
      }

      // dismiss stale delivered local-notification tray entries (yesterday's "go to sleep"
      // reminder, missed meal reminders from previous days, etc). LocalNotifications.getDelivered
      // only sees notifications scheduled by the LocalNotifications plugin — `extra.day` for those
      // is the entry's LOCAL date (set by notification-builder.ts:142), so comparing against
      // `now_local.toISODate()` is the correct local-vs-local comparison. Server-FCM pushes are
      // delivered via PushNotifications and NOT visible here; their tray entries will linger
      // until the user dismisses them (Capacitor PushNotifications has no per-id remove on Android).
      try {
        const todayLocalISO = now_local.toISODate();
        const delivered = await LocalNotifications.getDeliveredNotifications();
        const stale = (delivered.notifications as DeliveredEntry[]).filter(n => {
          const day = n.extra?.day;
          return day && todayLocalISO && day < todayLocalISO;
        });
        if (stale.length > 0) {
          await LocalNotifications.removeDeliveredNotifications({ notifications: stale });
        }
      } catch (err) {
        // reminders from earlier days stay in the tray; scheduling below still runs
        this.logging.trackSilentProblem('StaleDeliveredNotificationCleanupFailed', { error: describeError(err) });
      }

      // load real log entries from the backlog cache (7 days ahead)
      const endDate = now_local.plus({ days: 7 });
      const entries: Log_Entry[] = await userDb.database.withDocWatcherAsync(
        watcher => userDb.getLogEntriesInRangeAsync(now_local, endDate, watcher));

      const getBody = (key: string) => CONTENT_BODY_GETTERS[key]?.() ?? key;

      // build two lists on purpose (#4087):
      // - the on-device (local) list EXCLUDES entries the user already completed or skipped, so a
      //   done/skipped occurrence is not re-scheduled locally on this reschedule.
      // - the server (cross-device push) list KEEPS them. The server stores one recurring weekly
      //   doc per slot; dropping a done/skipped entry from the POST would delete that shared doc
      //   and lose the reminder for FUTURE weeks too. Today's fire is instead silenced per-day by
      //   the suppressedDay stamp that suppressForEntryAsync set at done/skip time, which leaves the
      //   recurring doc intact. So the server list is the un-pruned build.
      const localNotifications = buildNotificationsFromLogEntries(entries, settings, now_local, getBody, { excludeActedEntries: true });
      const serverNotifications = buildNotificationsFromLogEntries(entries, settings, now_local, getBody);

      // merge in extra notifications (e.g. test notifications) into both lists
      if (extraNotifications) {
        localNotifications.push(...extraNotifications);
        serverNotifications.push(...extraNotifications);
      }

      if (localNotifications.length > 0) {
        // `sound: 'default'` is required on iOS — Capacitor's LocalNotificationsPlugin.swift
        // only sets UNNotificationContent.sound when a sound field is present, else the
        // notification is silent. On Android the 'default' string fails resource lookup
        // and the plugin falls back to the channel default, which plays the system sound.
        await LocalNotifications.schedule({
          notifications: localNotifications.map(n => ({ ...n, sound: 'default' })),
        });
        console.log(`NotificationService: scheduled ${localNotifications.length} notifications`);
      }

      // sync schedule to server for cross-device push (fire-and-forget)
      if (cloudFunctions) {
        void this.syncScheduleToServerAsync(serverNotifications, cloudFunctions);
      }
    } catch (err) {
      // if our own sign-out or recovery closed the database mid-read, there is nothing to
      // schedule for anyway. anything else leaves this device with no reminders until the
      // next reschedule, so report it
      if (isDatabaseTornDown(err)) {
        console.log('NotificationService: reschedule stopped, the database was closed:', err);
        return;
      }
      this.logging.trackSilentProblem('NotificationRescheduleFailed', { error: describeError(err) });
    }
  }
}
